import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { Input, type InputProps } from './input';

export interface SecretInputProps extends Omit<InputProps, 'type'> {
  /** Accessible label for the button while the value is hidden. */
  showLabel?: string;
  /** Accessible label for the button while the value is shown. */
  hideLabel?: string;
}

export interface SecretInputViewProps extends SecretInputProps {
  revealed: boolean;
  onToggle: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

/** The stateless markup behind {@link SecretInput}; exported so the toggle can be tested without a DOM. */
export function SecretInputView({
  revealed,
  onToggle,
  inputRef,
  showLabel = 'Show',
  hideLabel = 'Hide',
  className,
  disabled,
  ...props
}: SecretInputViewProps) {
  const label = revealed ? hideLabel : showLabel;
  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        {...props}
        type={revealed ? 'text' : 'password'}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-1p-ignore="true"
        data-lpignore="true"
        disabled={disabled}
        className={cn('pr-10', className)}
      />
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={label}
        aria-controls={props.id}
        title={label}
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {revealed ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  );
}

/** A password-style input for API keys, tokens, and passwords, with a Show / Hide toggle. */
const SecretInput = React.forwardRef<HTMLInputElement, SecretInputProps>((props, ref) => {
  const [revealed, setRevealed] = React.useState(false);
  return <SecretInputView {...props} inputRef={ref} revealed={revealed} onToggle={() => setRevealed((current) => !current)} />;
});
SecretInput.displayName = 'SecretInput';

export { SecretInput };
